import { useMemo, useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { DefaultPeriodOptionsColumn } from "Constants";
import { format } from "date-fns";
import { Branch } from "Models/branch";
import { Company } from "Models/company";
import { OrdersManagement, OrdersManagementCSV } from "Models/report.orders.management";
import { Searches } from "Models/shipeezi.table";
import { CompanyConnectionsService } from "Services/company.connections.service";
import JobTypeService from "Services/job.type.service";
import ModeJobService from "Services/mode.job.service";
import OrdersManagementReportService from "Services/orders.management.report.service";
import StatusesService from "Services/statuses.service";
import { uniqueArrayWithComparator } from "Util/uniqueArray";

import { useAppState } from "../../../../../../../context/StateContext";
import { UsedByStatuses } from "../../../../../../../util/enums";

const CACHE_TIME = 60 * 60 * 1000; // 1 hour
const STALE_TIME = 2 * 60 * 1000; // 2 minutes

interface ReportFilters {
  [x: string]: string[] | number[];
}
export const useActions = () => {
  const { currentUser } = useAppState();

  const [filters, setFilters] = useState<ReportFilters>({
    companyId: [String(currentUser?.companyId!)],
  });
  const [search, setSearch] = useState<Searches>([]);
  const [period, setPeriod] = useState<DefaultPeriodOptionsColumn>(
    DefaultPeriodOptionsColumn.CUSTOM_PERIOD
  );
  const defaultStartDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const defaultEndDate = new Date();

  const [companies, setCompanies] = useState<Company[]>();
  const [total, setTotal] = useState<number>(0);
  const [ordersManagementsReports, setOrdersManagementsReports] = useState<OrdersManagement[]>([]);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [startDate, setStartDate] = useState<Date | undefined>(defaultStartDate);
  const [endDate, setEndDate] = useState<Date | undefined>(defaultEndDate);
  const [openRows, setOpenRows] = useState<number[]>([]);

  const clearOpenRows = () => {
    setOpenRows([]);
  };

  const handleSearch = (value: Searches) => {
    setSearch(value);
    setPage(0);
  };

  useQuery({
    queryKey: ["companies-with-connection", currentUser?.companyId],
    queryFn: async () => {
      return CompanyConnectionsService.getCompaniesWithConnection().then(({ data }) => {
        setCompanies(data);

        return data;
      });
    },
    refetchOnWindowFocus: false,
    enabled: !!currentUser?.id,
    initialData() {
      return [];
    },
  });

  const ordersManagementsFilters = useMemo(() => {
    const companyIds = filters?.companyId?.length
      ? filters?.companyId
      : companies?.map((company) => company.id!);

    return {
      searchByOr: search && search.length > 0 ? JSON.stringify(search) : "",
      startDate: startDate && format(startDate, "yyyy-MM-dd"),
      endDate: endDate && format(endDate, "yyyy-MM-dd"),
      ...filters,
      companyId: JSON.stringify(companyIds),
      ...(filters?.status ? { status: JSON.stringify(filters.status) } : {}),
    };
  }, [filters, companies, search, startDate, endDate]);

  const enableGridAndWidgetQuerys = useMemo(() => {
    return !!(ordersManagementsFilters?.companyId?.length && startDate && endDate);
  }, [ordersManagementsFilters?.companyId, startDate, endDate]);

  const branchesList = useMemo(() => {
    if (currentUser?.branches?.length) {
      const branches = currentUser?.branches?.sort((branchA, branchB) =>
        (branchA?.name || "").localeCompare(branchB?.name || "")
      );

      const uniqueBranches = uniqueArrayWithComparator(branches, (a, b) => a.id === b.id);

      return uniqueBranches;
    }

    return [] as Branch[];
  }, [currentUser]);

  const getOrdersManagements = () =>
    useQuery(
      ["orders.management.reports.list", page, pageSize, ordersManagementsFilters],
      () =>
        OrdersManagementReportService.getOrdersManagementReports({
          ...ordersManagementsFilters,
          page,
          pageSize,
        }),
      {
        onSuccess: (response) => {
          if (response && response?.data) {
            setTotal(response.data.count);
            setOrdersManagementsReports(response.data.orders);
            clearOpenRows();
          }
        },
        enabled: enableGridAndWidgetQuerys,
      }
    );

  const getOrdersManagementsWidgets = () =>
    useQuery({
      queryKey: ["orders.management.widgets", ordersManagementsFilters],
      queryFn: async () => {
        const response = await OrdersManagementReportService.getOrdersManagementWidgets({
          ...ordersManagementsFilters,
        });

        if (response && response.data) return response.data;
      },
      enabled: enableGridAndWidgetQuerys,
    });

  const useGetJobType = () =>
    useQuery({
      queryKey: ["jobType.list"],
      queryFn: async () => {
        const response = await JobTypeService.getAll({
          companyId: currentUser?.companyId,
          sortBy: JSON.stringify([["name", "asc"]]),
        });

        if (response && response.data) return response.data;
      },
      enabled: !!currentUser?.companyId,
    });

  const useGetJobStatus = () =>
    useQuery({
      queryKey: ["statuses.list"],
      queryFn: async () => {
        const response = await StatusesService.getAll({
          usedBy: UsedByStatuses.ORDERS,
          companyId: currentUser?.companyId,
        });

        if (response && response.data) return response.data;
      },
      enabled: !!currentUser?.companyId,
    });

  const useGetJobModes = () =>
    useQuery({
      queryKey: ["mode.list"],
      queryFn: async () => {
        const response = await ModeJobService.getAll({
          companyId: currentUser?.companyId,
        });

        if (response && response.data) return response.data;
      },
      enabled: !!currentUser?.companyId,
    });

  const useExportOrderCSVData = (callback: (data: OrdersManagementCSV[]) => void) => {
    return useMutation({
      mutationKey: ["export.management.order.csv", ordersManagementsFilters],
      mutationFn: async () => {
        return OrdersManagementReportService.getOrdersManagementExportCSVData({
          ...ordersManagementsFilters,
        });
      },
      onSuccess: (response) => {
        if (response?.data?.length) {
          callback(response.data);
        }
      },
    });
  };

  return {
    companies,
    useGetJobType,
    useGetJobStatus,
    useGetJobModes,
    getOrdersManagementsWidgets,
    getOrdersManagements,
    ordersManagementsFilters,
    ordersManagementsReports,
    startDate,
    endDate,
    filters,
    page,
    pageSize,
    setEndDate,
    setFilters,
    setPage,
    setPageSize,
    setStartDate,
    total,
    useExportOrderCSVData,
    branchesList,
    handleSearch,
    setPeriod,
    period,
    defaultStartDate,
    defaultEndDate,
    setOpenRows,
    openRows,
  };
};
